import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import {
  Box,
  Card,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  TableContainer,
  TableFooter,
  TablePagination,
  Grid,
  TextField,
  MenuItem,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

import { CustomIconButton, DefaultContainedButton, DefaultOutlinedButton, RedOutlinedButton } from '../Buttons';
import { CommonLabel } from '../Common';
import { TablePaginationActions, theme } from '../TableStyles';
import { CustomAutocomplete, CustomTextField } from '../StyledInputs';
import API from '../../api';
import { LoadingUI } from '../LayoutStyles';
import { DecisionDialog } from '../DecisionDialog';
import { usePopup } from '../../context/PopupContext';

export default function EventsListView({ isAdmin, hasAuth }) {
  const navigate = useNavigate();
  const { showPopup, setPopUpText } = usePopup();

  const [eventsCelebrationsList, setEventsCelebrationsList] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isFetching, setIsFetching] = useState(true);

  // Pagination
  const [page, setPage] = useState(0);
  const rowsPerPage = 8;

  const emptyRows = Math.max(0, rowsPerPage - eventsCelebrationsList.length);

  // change page
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchEventsCelebrations(lastSubmittedFilters, newPage);
  };

  const [countriesList, setCountriesList] = useState([]);
  const [regionsList, setRegionsList] = useState([]);
  const [provincesList, setProvincesList] = useState([]);
  const [citiesList, setCitiesList] = useState([]);
  const [italy, setItaly] = useState({});

  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedRegionName, setSelectedRegionName] = useState(null);
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedCityName, setSelectedCityName] = useState(null);

  const [lastSubmittedFilters, setLastSubmittedFilters] = useState(null);

  const [filterTexts, setFilterTexts] = useState({
    name: '',
    description: '',
  });
  const getCountriesList = () => {
    API.get('/countries').then((response) => {
      const italyData = response.data.find((element) => element.name === 'Italia');
      setItaly(italyData);
      setCountriesList(response.data);
    });
  };

  //   Get Regions List
  useEffect(() => {
    if (selectedCountry !== null && selectedCountry !== undefined && selectedCountry !== '') {
      if (
        typeof selectedCountry === 'object' &&
        Object.keys(selectedCountry).length > 0 &&
        selectedCountry.name === italy.name
      ) {
        API.get(`/countries/regions`, {
          params: {
            id: selectedCountry.id,
          },
        }).then((response) => {
          if (response.data.length > 0) {
            setRegionsList(response.data);
          } else {
            setRegionsList([]);
            setProvincesList([]);
            setCitiesList([]);
          }
        });
      }
    }
  }, [selectedCountry]);

  useEffect(() => {
    if (selectedCountry && selectedCountry?.name !== italy.name) {
      setSelectedProvince('');
      setSelectedRegion('');
      setSelectedCity('');
    }

    if (selectedCountry?.name !== italy.name) {
      setRegionsList([]);
      setProvincesList([]);
      setCitiesList([]);
    }
  }, [selectedCountry]);

  //   Get Provinces List
  useEffect(() => {
    if (selectedRegion !== null && selectedRegion !== undefined && selectedRegion !== '') {
      if (typeof selectedRegion === 'object' && Object.keys(selectedRegion).length > 0) {
        API.get(`/regions/provinces`, {
          params: {
            region_name: selectedRegion.region_name,
          },
        }).then((response) => {
          if (response.data.length > 0) {
            setProvincesList(response.data);
          } else {
            setProvincesList([]);
            setCitiesList([]);
          }
        });
      }
    }
  }, [selectedRegion]);

  //   Get Cities List
  useEffect(() => {
    if (selectedProvince !== null && selectedProvince !== undefined && selectedProvince !== '') {
      if (typeof selectedProvince === 'object' && Object.keys(selectedProvince).length > 0) {
        API.get(`/provinces/cities`, {
          params: {
            id: selectedProvince.id,
          },
        }).then((response) => {
          if (response.data.length > 0) {
            setCitiesList(response.data);
          } else {
            setCitiesList([]);
          }
        });
      }
    }
  }, [selectedProvince]);

  useEffect(() => {
    if (italy && Object.keys(italy).length > 0) {
      setSelectedCountry(italy);
    }
  }, [italy]);

  const fetchEventsCelebrations = (filters, pageNumber) => {
    setIsFetching(true);

    const queryParams = new URLSearchParams();
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (key === '__estero') return;
      if (value !== null && value !== undefined && value !== '') {
        queryParams.append(key, value);
      }
    });
    queryParams.append('page', pageNumber + 1);
    queryParams.append('per_page', rowsPerPage);

    const estero = filters?.__estero ? '&estero' : '';

    API.get(`/events-celebrations?${queryParams.toString()}${estero}`)
      .then((response) => {
        setEventsCelebrationsList(response.data.data);
        setTotalItems(response.data.total);
      })
      .finally(() => setIsFetching(false));
  };

  useEffect(() => {
    fetchEventsCelebrations({}, 0);
    getCountriesList();
  }, []);

  const handleChangeFilterText = (key, event) => {
    setFilterTexts((prevTexts) => ({
      ...prevTexts,
      [key]: event.target.value,
    }));
  };

  const handleResetFilters = () => {
    setSelectedCity('');
    setSelectedRegionName('');
    setSelectedCityName('');
    setSelectedProvince('');
    setSelectedRegion('');
    setSelectedCountry(italy);
    setProvincesList([]);
    setCitiesList([]);
    setFilterTexts({
      name: '',
      description: '',
    });
    setOrderFilter({
      column: '',
      type: '',
    });
    setLastSubmittedFilters({});
    setPage(0);
    fetchEventsCelebrations({}, 0);
  };

  const handleSubmitFilters = (event) => {
    event.preventDefault();
    const filters = {
      title: filterTexts.name,
      description: filterTexts.description,
      country_id: selectedCountry?.id,
      region_name:
        selectedRegion && Object.keys(selectedRegion).length > 0 ? selectedRegion?.region_name : selectedRegionName,
      province_id: selectedProvince?.id,
      city_id: selectedCity?.id,
      city_name: selectedCityName,
      __estero: selectedCountry?.name !== italy.name,
    };

    if (orderFilter.column && orderFilter.type) {
      filters.sort_field = orderFilter.column;
      filters.sort_direction = orderFilter.type;
    }
    // Controlla se i filtri attuali sono uguali a quelli precedenti
    if (JSON.stringify(filters) === JSON.stringify(lastSubmittedFilters)) {
      return;
    }

    setLastSubmittedFilters(filters);
    setPage(0);
    fetchEventsCelebrations(filters, 0);
  };

  const orderColumns = [
    { label: 'Data Inizio', value: 'start_date' },
    { label: 'Data Fine', value: 'end_date' },
    { label: 'Titolo', value: 'title' },
  ];

  const orderTypes = [
    { label: 'Crescente', value: 'asc' },
    { label: 'Decrescente', value: 'desc' },
  ];

  const [orderFilter, setOrderFilter] = useState({
    column: '',
    type: '',
  });

  const handleChangeOrderColumn = (event, key) => {
    setOrderFilter((prevOrder) => ({
      ...prevOrder,
      [key]: event.target.value,
    }));
  };

  const [openDeleteEventDialog, setOpenDeleteEventDialog] = useState(false);
  const [eventToDelete, setEventToDelete] = useState({});

  // open delete calendar event dialog
  const handleOpenDeleteEventDialog = (event) => {
    setEventToDelete(event);
    setOpenDeleteEventDialog(true);
  };

  // close delete calendar event dialog
  const handleCloseDeleteEventDialog = () => {
    setEventToDelete({});
    setOpenDeleteEventDialog(false);
  };

  // Delete an event request
  const onDeleteEvent = () => {
    const eventType = eventToDelete.type;

    const url = eventType === 'event' ? `/events/${eventToDelete.id}` : `/celebrations/${eventToDelete.id}`;

    API.delete(url)
      .then((response) => {
        handleCloseDeleteEventDialog();

        fetchEventsCelebrations(lastSubmittedFilters, page);

        setPopUpText(response.data.message);
        showPopup(true);
      })
      .catch((error) => {
        setPopUpText('Si è verificato un errore');
        showPopup(false);
      });
  };

  return (
    <>
      <Card sx={{ padding: '24px', marginBottom: '12px' }}>
        <Box
          justifyContent="end"
          alignItems="center"
          gap={2}
          sx={{
            display: isAdmin && hasAuth ? 'flex' : 'none',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
          }}
        >
          <DefaultContainedButton
            onClick={() =>
              navigate('/admin/events/handler', {
                state: { redirectType: 'create', creationType: 'celebration', isAdmin },
              })
            }
            startIcon={<AddIcon />}
          >
            Nuova Festa
          </DefaultContainedButton>
          <DefaultContainedButton
            onClick={() =>
              navigate('/admin/events/handler', { state: { redirectType: 'create', creationType: 'event', isAdmin } })
            }
            startIcon={<AddIcon />}
          >
            Nuovo Evento
          </DefaultContainedButton>
        </Box>
        <Box sx={{ paddingY: '24px' }}>
          <Typography variant="h6" sx={{ pb: 1.5 }}>
            Filtri ricerca
          </Typography>
          <form onSubmit={handleSubmitFilters}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <CustomTextField
                  id="name"
                  label="Titolo"
                  value={filterTexts.name}
                  onChange={(e) => handleChangeFilterText('name', e)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <CustomTextField
                  id="description"
                  label="Descrizione"
                  multiline
                  value={filterTexts.description}
                  onChange={(e) => handleChangeFilterText('description', e)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <CustomAutocomplete
                  options={
                    selectedCountry?.name === italy.name
                      ? countriesList.filter(
                          (country) =>
                            (!selectedRegion || country.id === selectedRegion.country_id) &&
                            (!selectedProvince || country.id === selectedProvince.country_id)
                        )
                      : countriesList
                  }
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  getOptionLabel={(option) => option.name}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.id}>
                      {option.name}
                    </Box>
                  )}
                  value={selectedCountry}
                  onChange={(e, value) => setSelectedCountry(value)}
                  renderInput={(params) => <TextField {...params} label="Paese" variant="outlined" />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <CustomAutocomplete
                  freeSolo={selectedCountry?.name !== italy.name}
                  options={regionsList.filter(
                    (region) =>
                      (!selectedProvince || region.region_name === selectedProvince.region_name) &&
                      (!selectedCountry || region.country_id === selectedCountry.id)
                  )}
                  isOptionEqualToValue={(option, value) => option.region_name === value.region_name}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') {
                      return option;
                    }
                    if (option.inputValue) {
                      return option.inputValue;
                    }
                    return option.region_name;
                  }}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.region_name}>
                      {option.region_name}
                    </Box>
                  )}
                  value={
                    selectedRegionName === undefined ||
                    selectedRegionName === null ||
                    selectedCountry?.name === italy.name
                      ? selectedRegion
                      : selectedRegionName
                  }
                  onChange={(e, value) => {
                    if (typeof value === 'string') {
                      setSelectedRegion(value);
                    } else if (value && value.inputValue) {
                      setSelectedRegion(value.inputValue);
                    } else {
                      setSelectedRegion(value);
                    }
                  }}
                  onInputChange={(e, value) => {
                    if (selectedCountry?.name !== italy.name) {
                      setSelectedRegionName(value);
                    } else {
                      setSelectedRegionName(null);
                    }
                  }}
                  renderInput={(params) => <TextField {...params} label="Regione" variant="outlined" />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <CustomAutocomplete
                  freeSolo={selectedCountry?.name !== italy.name}
                  disabled={selectedCountry && selectedCountry?.name !== italy.name}
                  options={provincesList.filter(
                    (province) =>
                      (!selectedRegion || province.region_name === selectedRegion.region_name) &&
                      (!selectedCity || province.id === selectedCity.province_id) &&
                      (!selectedCountry || province.country_id === selectedCountry.id)
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') {
                      return option;
                    }
                    if (option.inputValue) {
                      return option.inputValue;
                    }
                    return option.name;
                  }}
                  value={selectedProvince}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.id}>
                      {option.name}
                    </Box>
                  )}
                  onChange={(e, value) => {
                    if (typeof value === 'string') {
                      setSelectedProvince(value);
                    } else if (value && value.inputValue) {
                      setSelectedProvince(value.inputValue);
                    } else {
                      setSelectedProvince(value);
                    }
                  }}
                  renderInput={(params) => <TextField {...params} label="Provincia" variant="outlined" />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <CustomAutocomplete
                  freeSolo={selectedCountry?.name !== italy.name}
                  options={citiesList.filter((city) => !selectedProvince || city.province_id === selectedProvince.id)}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') {
                      return option;
                    }
                    if (option.inputValue) {
                      return option.inputValue;
                    }
                    return option.name;
                  }}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.id}>
                      {option.name}
                    </Box>
                  )}
                  value={
                    selectedCityName === undefined || selectedCityName === null || selectedCountry?.name === italy.name
                      ? selectedCity
                      : selectedCityName
                  }
                  onChange={(e, value) => {
                    if (typeof value === 'string') {
                      setSelectedCity(value);
                    } else if (value && value.inputValue) {
                      setSelectedCity(value.inputValue);
                    } else {
                      setSelectedCity(value);
                    }
                  }}
                  onInputChange={(e, value) => {
                    if (selectedCountry?.name !== italy.name) {
                      setSelectedCityName(value);
                    } else {
                      setSelectedCityName(null);
                    }
                  }}
                  renderInput={(params) => <TextField {...params} label="Comune" variant="outlined" />}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6">Ordinamento</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <CustomTextField
                  id="orderColumn"
                  label="Colonna"
                  select
                  value={orderFilter.column}
                  onChange={(e) => handleChangeOrderColumn(e, 'column')}
                >
                  <MenuItem value="">
                    <em>Nessuno</em>
                  </MenuItem>
                  {orderColumns.map((column) => (
                    <MenuItem key={column.value} value={column.value}>
                      {column.label}
                    </MenuItem>
                  ))}
                </CustomTextField>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <CustomTextField
                  id="orderType"
                  label="Ordinamento"
                  select
                  value={orderFilter.type}
                  onChange={(e) => handleChangeOrderColumn(e, 'type')}
                >
                  <MenuItem value="">
                    <em>Nessuno</em>
                  </MenuItem>
                  {orderTypes.map((column) => (
                    <MenuItem key={column.value} value={column.value}>
                      {column.label}
                    </MenuItem>
                  ))}
                </CustomTextField>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <DefaultOutlinedButton
                  onClick={handleResetFilters}
                  customStyles={{ width: '100%', height: '56px', backgroundColor: '#f4f6f8' }}
                >
                  Svuota Filtri / Ordinamento
                </DefaultOutlinedButton>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <DefaultContainedButton type="submit" customStyles={{ width: '100%', height: '56px' }}>
                  Cerca
                </DefaultContainedButton>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Card>
      <Card sx={{ padding: '24px' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Tipologia</TableCell>
                <TableCell>Titolo</TableCell>
                <TableCell>Città dove si svolge l'evento</TableCell>
                <TableCell>Data Inizio</TableCell>
                <TableCell>Data Fine</TableCell>
                <TableCell>Stato</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>

            {isFetching ? (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={7}>{LoadingUI()}</TableCell>
                </TableRow>
              </TableBody>
            ) : eventsCelebrationsList.length > 0 ? (
              <TableBody>
                {eventsCelebrationsList.map((ec) => (
                  <TableRow key={ec.id} hover>
                    <TableCell>
                      <CommonLabel
                        textColor={ec.type === 'celebration' ? '#1177a4' : '#b76e00'}
                        bgColor={ec.type === 'celebration' ? '#d6f4f9' : '#fff1d6'}
                        text={ec.type === 'celebration' ? 'Festa' : 'Evento'}
                      />
                    </TableCell>

                    <TableCell>{ec.title}</TableCell>
                    <TableCell>{ec.city}</TableCell>
                    <TableCell>{dayjs(ec.start_date).format('DD/MM/YYYY')}</TableCell>
                    <TableCell>{dayjs(ec.end_date).format('DD/MM/YYYY')}</TableCell>
                    <TableCell>
                      <CommonLabel
                        textColor={ec.status === 'visible' ? '#118d57' : '#b71d18'}
                        bgColor={ec.status === 'visible' ? '#d3efdf' : '#f7ddd8'}
                        text={ec.status === 'visible' ? 'Pubblico' : 'Bozza'}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" justifyContent="end">
                        {/* SHow Celebration */}
                        <Box>
                          <Tooltip
                            title={ec.type === 'celebration' ? 'Visualizza festa' : 'Visualizza evento'}
                            arrow
                            placement="top"
                          >
                            <Box>
                              <CustomIconButton
                                onClick={() => {
                                  const path = isAdmin ? '/admin/events/handler' : '/user/events/handler';

                                  navigate(path, {
                                    state: { event: ec, redirectType: 'show', isAdmin },
                                  });
                                }}
                              >
                                <DescriptionOutlinedIcon />
                              </CustomIconButton>
                            </Box>
                          </Tooltip>
                        </Box>

                        {/* Delete Celebration */}
                        <Box sx={{ display: isAdmin && hasAuth ? 'flex' : 'none' }}>
                          <Tooltip
                            title={ec.type === 'celebration' ? 'Elimina festa' : 'Elimina evento'}
                            arrow
                            placement="top"
                          >
                            <Box>
                              <CustomIconButton onClick={() => handleOpenDeleteEventDialog(ec)}>
                                <DeleteIcon />
                              </CustomIconButton>
                            </Box>
                          </Tooltip>
                        </Box>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}

                {emptyRows > 0 && (
                  <TableRow style={{ height: 72 * emptyRows }}>
                    <TableCell colSpan={5} />
                  </TableRow>
                )}
              </TableBody>
            ) : (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center' }}>
                    Nessun evento disponibile
                  </TableCell>
                </TableRow>
              </TableBody>
            )}

            <TableFooter>
              <TableRow>
                <ThemeProvider theme={theme}>
                  <TablePagination
                    sx={{ borderBottom: 'none' }}
                    rowsPerPageOptions={[]}
                    colSpan={7}
                    count={totalItems}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    labelRowsPerPage="Righe per Pagina"
                    ActionsComponent={TablePaginationActions}
                  />
                </ThemeProvider>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Card>

      <DecisionDialog
        openDialog={openDeleteEventDialog}
        dialogTitle="Eliminazione evento"
        dialogDescription={
          <>
            Sei sicuro di voler eliminare l'evento {eventToDelete.title}? <br />
            Verranno eliminate anche tutte le date e gli stand ad esso relazionati.
          </>
        }
        closeOperation={handleCloseDeleteEventDialog}
        requestOperation={onDeleteEvent}
        requestText={'Elimina'}
        closeText={'Chiudi'}
        RequestButton={RedOutlinedButton}
      />
    </>
  );
}
